'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { DateTime, Settings } from 'luxon';
import { Divider } from '../../../../core-components';
type DashboardStageOverviewCountdownProps = {
  dateRezoned: string;
  nowOffsetInSeconds: number;
  className?: string;
  textHours?: string;
  textMinutes?: string;
  textDays?: string;
};
export const DashboardStageOverviewCountdown: FC<DashboardStageOverviewCountdownProps> = ({
  dateRezoned,
  nowOffsetInSeconds,
  className,
  textHours = 'Stunden',
  textMinutes = 'Minuten',
  textDays = 'Tage'
}) => {
  const [diffInSeconds, setDiffInSeconds] = useState(() => {
    return getDiffInSeconds(dateRezoned, nowOffsetInSeconds);
  });
  const [firstSlot, secondSlot] = useMemo(() => {
    if (diffInSeconds < 0) {
      return [{
        value: 0,
        label: textHours
      }, {
        value: 0,
        label: textMinutes
      }];
    }
    const diffMinutes = Math.ceil(diffInSeconds / 60);
    const days = Math.floor(diffMinutes / 1_440);
    const hours = Math.floor(diffMinutes % 1_440 / 60);
    const minutes = Math.floor(diffMinutes % 1_440 % 60);
    if (days > 0) {
      return [{
        value: days,
        label: textDays
      }, {
        value: hours,
        label: textHours
      }];
    }
    return [{
      value: hours,
      label: textHours
    }, {
      value: minutes,
      label: textMinutes
    }];
  }, [textDays, textHours, textMinutes, diffInSeconds]);
  useEffect(() => {
    const interval = setInterval(() => {
      setDiffInSeconds(getDiffInSeconds(dateRezoned, nowOffsetInSeconds));
    }, 60_000 /* every minute */);
    return () => clearInterval(interval);
  });
  return <div className={twMerge('bg-white flex rounded-md gap-4 py-2 px-4 md:py-4 md:px-6 lg:py-6 lg:px-8 text-high-emphasis', className)} data-sentry-component="DashboardStageOverviewCountdown" data-sentry-source-file="Countdown.tsx">
      <div className="flex flex-col items-center text-center w-16">
        <p className={twMerge('headline-lg md:headline-xl')}>
          {firstSlot.value.toString().padStart(2, '0')}
        </p>
        <p className="text-base">{firstSlot.label}</p>
      </div>
      <div className="flex items-center justify-center">
        <Divider align="vertical" className="h-10" data-sentry-element="Divider" data-sentry-source-file="Countdown.tsx" />
      </div>
      <div className="flex flex-col items-center text-center w-16">
        <p className={twMerge('headline-lg md:headline-xl')}>
          {secondSlot.value.toString().padStart(2, '0')}
        </p>
        <p className="text-base">{secondSlot.label}</p>
      </div>
    </div>;
};
const getDiffInSeconds = (date: string, nowOffsetInSeconds: number) => {
  const now = toRezonedDateTime(DateTime.now()).plus({
    second: nowOffsetInSeconds
  });
  return toRezonedDateTime(date).toUnixInteger() - now.toUnixInteger();
};
function toRezonedDateTime(date: undefined): undefined;
function toRezonedDateTime(date: DateTime | Date | string): DateTime;
function toRezonedDateTime(date: DateTime | Date | string | undefined): DateTime | undefined {
  if (typeof date === 'undefined') {
    return undefined;
  }
  if (typeof date === 'string') {
    date = DateTime.fromISO(date, {
      setZone: true
    });
  }
  if (!('year' in date)) {
    date = DateTime.fromJSDate(date);
  }
  return date.setZone(Settings.defaultZone, {
    keepLocalTime: true
  });
}